// Enhanced Cash Register Service with improved architecture
import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';
import {
  createEmptyNewCashRegisterData,
  normalizeNewCashRegisterData,
  isCorruptedKvString,
  parseKvObject
} from '../utils/cashRegisterDataStorage';
import { normalizeBusinessStorageId } from '../utils/sharedCore';
import { readExpensesSyncForBusiness } from '../utils/expensesReadSource';
import { readArrayFieldFromMergedBlob } from '../utils/businessStateSync';
import {
  computeSessionLiquidBalance,
  computeSessionExpectedFromWorkerSession,
  resolveSessionOutflows,
  sumRegisterCashOutflows,
  CASH_REGISTER_OUTFLOW_TYPES
} from '../domain/finance/sessionCashflow';

class NewCashRegisterService {
  constructor() {
    this.storageKey = 'newCashRegisterData';
    this.sessionKey = 'cashRegisterSessions';
  }

  // Get business-specific storage key
  getStorageKey(businessId) {
    const bid = businessId ? normalizeBusinessStorageId(businessId) : '';
    return bid ? `${this.storageKey}_${bid}` : this.storageKey;
  }

  // Get session-specific storage key
  getSessionKey(businessId) {
    const bid = businessId ? normalizeBusinessStorageId(businessId) : '';
    return bid ? `${this.sessionKey}_${bid}` : this.sessionKey;
  }

  createEmptyBusinessData(businessId) {
    return createEmptyNewCashRegisterData(businessId);
  }

  // Initialize cash register data for a business
  initializeBusinessData(businessId) {
    const data = this.createEmptyBusinessData(businessId);
    this.saveBusinessData(data, businessId);
    return data;
  }

  normalizeBusinessData(data, businessId) {
    return normalizeNewCashRegisterData(data, businessId);
  }

  // Get business data
  getBusinessData(businessId) {
    try {
      const key = this.getStorageKey(businessId);
      const engine = getStorageEngine();
      const raw = engine.get(KV_COLLECTION, key);
      if (raw === undefined || raw === null) {
        return this.initializeBusinessData(businessId);
      }

      if (typeof raw === 'string' && isCorruptedKvString(raw)) {
        console.warn('⚠️ [CASH REGISTER] Corrupted business data, cleaning up');
        engine.delete(KV_COLLECTION, key);
        return this.initializeBusinessData(businessId);
      }

      const { corrupted, data } = parseKvObject(raw);
      if (corrupted || data == null) {
        if (corrupted) {
          console.error('Error parsing business data');
          engine.delete(KV_COLLECTION, key);
        }
        return this.initializeBusinessData(businessId);
      }

      return this.normalizeBusinessData(data, businessId);
    } catch (error) {
      console.error('Error getting business data:', error);
      return this.initializeBusinessData(businessId);
    }
  }

  // Save business data
  saveBusinessData(data, businessId) {
    try {
      const key = this.getStorageKey(businessId);
      data.lastUpdated = new Date().toISOString();
      const engine = getStorageEngine();
      engine.put(KV_COLLECTION, key, data);
      return true;
    } catch (error) {
      console.error('Error saving business data:', error);
      return false;
    }
  }

  // Create a new session
  createSession(workerId, workerName, initialAmount, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      const sessionId = this.generateSessionId();
      
      // Check if there's already an active session
      if (businessData.activeSession) {
        throw new Error('Active session already exists');
      }

      const session = {
        sessionId,
        workerId,
        workerName,
        businessId,
        loginTime: new Date().toISOString(),
        logoutTime: null,
        initialAmount: parseFloat(initialAmount) || 0,
        finalAmount: null,
        status: 'active',
        transactions: [],
        deviations: [],
        notes: '',
        createdAt: new Date().toISOString()
      };

      businessData.activeSession = session;
      businessData.sessions.push(session);
      businessData.statistics.totalSessions++;
      businessData.statistics.lastSessionDate = new Date().toISOString();

      this.saveBusinessData(businessData, businessId);
      return { success: true, session };
    } catch (error) {
      console.error('Error creating session:', error);
      return { success: false, error: error.message };
    }
  }

  // End a session
  endSession(workerId, finalAmount, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      
      if (!businessData.activeSession) {
        throw new Error('No active session found');
      }

      if (businessData.activeSession.workerId !== workerId) {
        throw new Error('Session worker mismatch');
      }

      const session = businessData.activeSession;
      session.logoutTime = new Date().toISOString();
      session.finalAmount = parseFloat(finalAmount) || 0;
      session.status = 'completed';

      // Calculate session statistics
      const sessionDuration = new Date(session.logoutTime) - new Date(session.loginTime);
      session.duration = Math.floor(sessionDuration / (1000 * 60)); // minutes

      // Update business statistics
      businessData.statistics.totalTransactions += session.transactions.length;
      businessData.statistics.totalCashHandled += session.finalAmount;

      // Clear active session
      businessData.activeSession = null;

      this.saveBusinessData(businessData, businessId);
      return { success: true, session };
    } catch (error) {
      console.error('Error ending session:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sessions historiques laissées « actives » sans activeSession (logout sessionRecorder seul).
   */
  repairOrphanSessions(businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      if (businessData.activeSession) {
        return { repaired: 0 };
      }

      let repaired = 0;
      const now = new Date().toISOString();
      (businessData.sessions || []).forEach((session) => {
        if (!session || session.logoutTime) return;
        if (session.status && session.status !== 'active') return;
        session.logoutTime = now;
        session.status = 'completed';
        session.finalAmount =
          session.finalAmount ?? session.finalCashAmount ?? session.initialAmount ?? 0;
        repaired += 1;
      });

      if (repaired > 0) {
        this.saveBusinessData(businessData, businessId);
      }
      return { repaired };
    } catch (error) {
      console.error('Error repairing orphan cash register sessions:', error);
      return { repaired: 0 };
    }
  }

  forceClearActiveSession(businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      if (!businessData.activeSession) {
        return { success: true, cleared: false };
      }
      businessData.activeSession = null;
      this.saveBusinessData(businessData, businessId);
      return { success: true, cleared: true };
    } catch (error) {
      console.error('Error clearing active cash register session:', error);
      return { success: false, error: error.message };
    }
  }

  // Pause a session
  pauseSession(workerId, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      
      if (!businessData.activeSession) {
        throw new Error('No active session found');
      }

      if (businessData.activeSession.workerId !== workerId) {
        throw new Error('Session worker mismatch');
      }

      const session = businessData.activeSession;
      session.status = 'paused';
      session.pausedAt = new Date().toISOString();

      this.saveBusinessData(businessData, businessId);
      return { success: true, session };
    } catch (error) {
      console.error('Error pausing session:', error);
      return { success: false, error: error.message };
    }
  }

  // Resume a session
  resumeSession(workerId, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      
      if (!businessData.activeSession) {
        throw new Error('No active session found');
      }

      if (businessData.activeSession.workerId !== workerId) {
        throw new Error('Session worker mismatch');
      }

      const session = businessData.activeSession;
      session.status = 'active';
      session.resumedAt = new Date().toISOString();

      this.saveBusinessData(businessData, businessId);
      return { success: true, session };
    } catch (error) {
      console.error('Error resuming session:', error);
      return { success: false, error: error.message };
    }
  }

  /** Bargeld-Soll: Anfang + Verkäufe − Dépenses/Achats (Bar) — gleiche Inputs wie Attendu. */
  getSessionCashBalance(businessId) {
    try {
      const session = this.getActiveSession(businessId);
      if (!session) return 0;

      const initial = Number(session.initialAmount) || 0;
      // cash_in from Créance-Einzug is already in session cashDebtCollections — do not double-add
      const journalCashInOnly = (session.transactions || [])
        .filter((t) => {
          if (!t || t.type !== 'cash_in' || t.voided === true) return false;
          const reason = String(t.reason || '').toLowerCase();
          if (reason === 'customer_debt_payment') return false;
          if (String(t.transactionType || '').toLowerCase() === 'customer_debt_payment') {
            return false;
          }
          return true;
        })
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
      const journalSaleIncome = (session.transactions || [])
        .filter((t) => t && t.type === 'sale' && t.voided !== true)
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
      const txOut = sumRegisterCashOutflows(session, session.loginTime, session.logoutTime);

      let dataOut = 0;
      let salesIncome = 0;
      let debtCashIncome = 0;
      let flowOk = false;
      try {
        const bid = businessId;
        const sessionFlow = computeSessionLiquidBalance({
          initialCash: 0,
          sales: Array.isArray(session.sales) ? session.sales : [],
          expenses: readExpensesSyncForBusiness(bid) || [],
          supermarketPayments: readArrayFieldFromMergedBlob(bid, 'supermarketPayments') || [],
          invoicePayments: readArrayFieldFromMergedBlob(bid, 'invoicePayments') || [],
          customerDebts: readArrayFieldFromMergedBlob(bid, 'customerDebts') || [],
          loginTime: session.loginTime,
          logoutTime: session.logoutTime,
          sessionWorkerId: session.workerId ?? null,
          allSales: readArrayFieldFromMergedBlob(bid, 'sales') || []
        });
        salesIncome = Number(sessionFlow.cashSales) || 0;
        debtCashIncome = Number(sessionFlow.cashDebtCollections) || 0;
        dataOut = resolveSessionOutflows(
          sessionFlow,
          session,
          session.loginTime,
          session.logoutTime,
          session.workerId ?? null
        ).cashOutgoing;
        flowOk = true;
      } catch {
        dataOut = txOut;
      }

      // Session-Sales sind SSOT für Sale-Income (auch 0 nach Vollstorno).
      // Journal-sale nur Fallback wenn Flow fehlt; cash_in (z. B. Reversals) immer additiv.
      const saleIncome = flowOk ? salesIncome : journalSaleIncome;
      const income = saleIncome + debtCashIncome + journalCashInOnly;
      const outflow = Math.max(txOut, dataOut);
      return initial + income - outflow;
    } catch {
      return 0;
    }
  }

  /**
   * Mark sale journal rows voided / amount-adjusted after sale void.
   */
  markSaleTransactionsVoided(businessId, saleId, { amount = null, fullyCancelled = true } = {}) {
    try {
      const businessData = this.getBusinessData(businessId);
      const sid = String(saleId);
      let marked = 0;
      const touch = (session) => {
        if (!session || !Array.isArray(session.transactions)) return;
        for (const t of session.transactions) {
          if (!t || t.type !== 'sale') continue;
          if (String(t.saleId ?? '') !== sid) continue;
          if (fullyCancelled) {
            if (t.voided === true) continue;
            t.voided = true;
            t.voidedAt = new Date().toISOString();
            marked += 1;
          } else if (amount != null && Number.isFinite(Number(amount))) {
            t.amount = Math.max(0, Number(amount) || 0);
            t.amountAdjustedAt = new Date().toISOString();
            marked += 1;
          }
        }
      };
      touch(businessData.activeSession);
      for (const session of Array.isArray(businessData.sessions) ? businessData.sessions : []) {
        touch(session);
      }
      if (marked > 0) {
        this.saveBusinessData(businessData, businessId);
      }
      return { success: true, marked };
    } catch (error) {
      console.error('Error marking sale transactions voided:', error);
      return { success: false, marked: 0, error: error.message };
    }
  }

  // Get active session
  getActiveSession(businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      return businessData.activeSession;
    } catch (error) {
      console.error('Error getting active session:', error);
      return null;
    }
  }

  // Get previous session's final amount
  getPreviousSessionAmount(businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      const sessions = Array.isArray(businessData.sessions) ? businessData.sessions : [];
      const completedSessions = sessions.filter((s) => s && s.status === 'completed');
      
      if (completedSessions.length === 0) {
        return null;
      }

      // Sort by logout time and get the most recent
      const sortedSessions = completedSessions.sort((a, b) => 
        new Date(b.logoutTime) - new Date(a.logoutTime)
      );

      return sortedSessions[0].finalAmount;
    } catch (error) {
      console.error('Error getting previous session amount:', error);
      return null;
    }
  }

  // Add transaction to active session
  addTransaction(transaction, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      
      if (!businessData.activeSession) {
        throw new Error('No active session found');
      }

      const session = businessData.activeSession;
      const transactionId = this.generateTransactionId();
      
    const newTransaction = {
      transactionId,
      ...transaction,
      timestamp: new Date().toISOString(),
      addedBy: transaction.workerId || session.workerId,
      workerId: transaction.workerId || session.workerId || null,
      workerName: transaction.workerName || session.workerName || null,
      actorRole: transaction.actorRole || null
    };

      session.transactions.push(newTransaction);
      businessData.statistics.totalTransactions++;

      this.saveBusinessData(businessData, businessId);
      return { success: true, transaction: newTransaction };
    } catch (error) {
      console.error('Error adding transaction:', error);
      return { success: false, error: error.message };
    }
  }

  // Add deviation to session
  addDeviation(deviation, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      
      if (!businessData.activeSession) {
        throw new Error('No active session found');
      }

      const session = businessData.activeSession;
      const deviationId = this.generateDeviationId();
      
      const newDeviation = {
        deviationId,
        ...deviation,
        timestamp: new Date().toISOString(),
        status: 'pending'
      };

      session.deviations.push(newDeviation);

      this.saveBusinessData(businessData, businessId);
      return { success: true, deviation: newDeviation };
    } catch (error) {
      console.error('Error adding deviation:', error);
      return { success: false, error: error.message };
    }
  }

  // Calculate session deviation
  calculateSessionDeviation(sessionId, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      const session = businessData.sessions.find(s => s.sessionId === sessionId);
      
      if (!session) {
        throw new Error('Session not found');
      }

      let expectedAmount;
      try {
        const flow = computeSessionExpectedFromWorkerSession(session, {
          expenses: readExpensesSyncForBusiness(businessId) || [],
          supermarketPayments:
            readArrayFieldFromMergedBlob(businessId, 'supermarketPayments') || [],
          invoicePayments: readArrayFieldFromMergedBlob(businessId, 'invoicePayments') || [],
          registerSession: session,
          allSales: readArrayFieldFromMergedBlob(businessId, 'sales') || []
        });
        expectedAmount = Number(flow.expectedCash) || 0;
      } catch {
        // Journal fallback: income − outflows (never add outflows).
        const initial = Number(session.initialAmount) || 0;
        let income = 0;
        let outflow = 0;
        for (const t of session.transactions || []) {
          if (!t || t.voided === true) continue;
          const amt = Math.abs(Number(t.amount) || 0);
          if (t.type === 'sale' || t.type === 'cash_in') income += amt;
          else if (CASH_REGISTER_OUTFLOW_TYPES.has(t.type)) outflow += amt;
        }
        expectedAmount = initial + income - outflow;
      }
      
      const actualAmount = session.finalAmount || 0;
      const difference = actualAmount - expectedAmount;
      
      const deviation = {
        sessionId,
        expectedAmount,
        actualAmount,
        difference,
        type: difference > 0 ? 'surplus' : difference < 0 ? 'shortage' : 'exact',
        percentage: expectedAmount > 0 ? (difference / expectedAmount) * 100 : 0,
        calculatedAt: new Date().toISOString()
      };

      return deviation;
    } catch (error) {
      console.error('Error calculating session deviation:', error);
      return null;
    }
  }

  // Get session history
  getSessionHistory(businessId, limit = 50) {
    try {
      const businessData = this.getBusinessData(businessId);
      return businessData.sessions
        .filter(s => s.status === 'completed')
        .sort((a, b) => new Date(b.logoutTime) - new Date(a.logoutTime))
        .slice(0, limit);
    } catch (error) {
      console.error('Error getting session history:', error);
      return [];
    }
  }

  // Get business statistics
  getBusinessStatistics(businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      const sessions = businessData.sessions.filter(s => s.status === 'completed');
      
      const statistics = {
        ...businessData.statistics,
        totalSessions: sessions.length,
        totalTransactions: sessions.reduce((sum, s) => sum + s.transactions.length, 0),
        totalCashHandled: sessions.reduce((sum, s) => sum + s.finalAmount, 0),
        averageSessionDuration: sessions.length > 0 ? 
          sessions.reduce((sum, s) => sum + (s.duration || 0), 0) / sessions.length : 0,
        deviationsCount: sessions.reduce((sum, s) => sum + s.deviations.length, 0),
        lastActivity: businessData.lastUpdated
      };

      return statistics;
    } catch (error) {
      console.error('Error getting business statistics:', error);
      return null;
    }
  }

  // Export session data
  exportSessionData(sessionId, businessId) {
    try {
      const businessData = this.getBusinessData(businessId);
      const session = businessData.sessions.find(s => s.sessionId === sessionId);
      
      if (!session) {
        throw new Error('Session not found');
      }

      const deviation = this.calculateSessionDeviation(sessionId, businessId);
      
      return {
        session,
        deviation,
        businessInfo: {
          businessId,
          exportDate: new Date().toISOString(),
          version: '2.0'
        }
      };
    } catch (error) {
      console.error('Error exporting session data:', error);
      return null;
    }
  }

  // Clear all data for a business
  clearBusinessData(businessId) {
    try {
      const key = this.getStorageKey(businessId);
      getStorageEngine().delete(KV_COLLECTION, key);
      return true;
    } catch (error) {
      console.error('Error clearing business data:', error);
      return false;
    }
  }

  // Generate unique IDs
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateTransactionId() {
    return `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateDeviationId() {
    return `dev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get current business ID
  getCurrentBusinessId() {
    try {
      const engine = getStorageEngine();
      const currentBusiness = engine.get(KV_COLLECTION, 'currentBusiness');
      if (currentBusiness === undefined || currentBusiness === null) {
        return null;
      }
      let business;
      if (typeof currentBusiness === 'string') {
        if (currentBusiness === '[object Object]' || currentBusiness === '"[object Object]"' || currentBusiness.trim() === '') {
          console.warn('⚠️ [CASH REGISTER] Corrupted currentBusiness data, cleaning up');
          engine.delete(KV_COLLECTION, 'currentBusiness');
          return null;
        }
        try {
          business = JSON.parse(currentBusiness);
        } catch (parseError) {
          console.error('Error parsing currentBusiness in newCashRegisterService:', parseError);
          engine.delete(KV_COLLECTION, 'currentBusiness');
          return null;
        }
      } else if (typeof currentBusiness === 'object' && currentBusiness !== null) {
        business = currentBusiness;
      } else {
        return null;
      }
      return business?.id || null;
    } catch (error) {
      console.error('Error getting current business ID:', error);
      return null;
    }
  }
}

// Create singleton instance
const newCashRegisterService = new NewCashRegisterService();

export default newCashRegisterService;
